import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Eyebrow, Reveal } from "@/components/Reveal";

const EMPTY = { name: "", company: "", email: "", phone: "", commodity: "", quantity: "", destination: "", incoterm: "", message: "" };

const MAP_EMBED =
  "https://maps.google.com/maps?q=Flydesk,%20Ayyappa%20Society%20Main%20Rd,%20Madhapur,%20Hyderabad,%20Telangana%20500081&t=&z=15&ie=UTF8&iwloc=&output=embed";

const inputCls =
  "w-full rounded-xl border border-stone2 bg-white px-4 py-3 text-sm text-ink placeholder:text-pebble/70 outline-none transition-colors duration-300 focus:border-gold focus:ring-2 focus:ring-gold/20";

export default function QuoteForm({ products, commodity, setCommodity }) {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (commodity) setForm((f) => ({ ...f, commodity }));
  }, [commodity]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry sent! Our export desk will reach out within 24 hours.");
      setForm(EMPTY);
      setCommodity("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="bg-night py-20 md:py-28">
      <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-8 lg:grid-cols-5 lg:gap-16 lg:px-16">
        <div className="lg:col-span-2" id="contact">
          <Reveal>
            <Eyebrow dark>Get a Personalized Quote</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight text-cream sm:text-4xl lg:text-5xl">
              Let's talk bulk pricing
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-cream/65 sm:text-base">
              Tell us the commodity, quantity and destination port — our export desk responds with a
              detailed quotation, samples plan and shipping schedule within one business day.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 space-y-5">
              <a href="mailto:info@tsugivainternational.com" data-testid="contact-email-link" className="flex items-center gap-4 text-cream/85 transition-colors hover:text-amberwarm">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/[0.06] text-amberwarm"><Mail size={18} /></span>
                <span className="text-sm">info@tsugivainternational.com <span className="block text-xs text-cream/50">Replies within 24 hours</span></span>
              </a>
              <div className="flex items-center gap-4 text-cream/85">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/[0.06] text-amberwarm"><MapPin size={18} /></span>
                <span className="text-sm">302, 3rd Floor, Flydesk, #901, Ayyappa Society Main Rd, <span className="block text-xs text-cream/50">SBH Officers Colony, Mega Hills, Madhapur, Hyderabad, Telangana 500081</span></span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-8 overflow-hidden rounded-2xl border border-cream/10">
              <iframe
                data-testid="contact-map-embed"
                title="Tsugiva International Traders — Madhapur, Hyderabad"
                src={MAP_EMBED}
                className="h-52 w-full grayscale-[35%] contrast-[1.05]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-3">
          <form
            data-testid="quote-form"
            onSubmit={submit}
            className="rounded-3xl border border-cream/10 bg-white/[0.05] p-6 backdrop-blur-sm sm:p-9"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <input data-testid="quote-form-name-input" required placeholder="Your Name *" value={form.name} onChange={set("name")} className={inputCls} />
              <input data-testid="quote-form-company-input" placeholder="Company" value={form.company} onChange={set("company")} className={inputCls} />
              <input data-testid="quote-form-email-input" required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} />
              <input data-testid="quote-form-phone-input" placeholder="Phone / WhatsApp" value={form.phone} onChange={set("phone")} className={inputCls} />
              <select
                data-testid="quote-form-commodity-select"
                required
                value={form.commodity}
                onChange={set("commodity")}
                className={`${inputCls} ${form.commodity ? "" : "text-pebble/70"}`}
              >
                <option value="" disabled>Select Commodity *</option>
                {products.map((p) => (
                  <option key={p.id} value={p.name}>{p.name}</option>
                ))}
                <option value="Other">Other / Mixed Container</option>
              </select>
              <input data-testid="quote-form-quantity-input" placeholder="Quantity (MT / Litres)" value={form.quantity} onChange={set("quantity")} className={inputCls} />
              <input data-testid="quote-form-destination-input" placeholder="Destination Port / Country" value={form.destination} onChange={set("destination")} className={inputCls} />
              <select
                data-testid="quote-form-incoterm-select"
                value={form.incoterm}
                onChange={set("incoterm")}
                className={`${inputCls} ${form.incoterm ? "" : "text-pebble/70"}`}
              >
                <option value="" disabled>Preferred Incoterm</option>
                <option value="FOB">FOB — Free on Board</option>
                <option value="CIF">CIF — Cost, Insurance & Freight</option>
                <option value="CFR">CFR — Cost & Freight</option>
                <option value="EXW">EXW — Ex Works</option>
              </select>
              <textarea
                data-testid="quote-form-message-input"
                rows={4}
                placeholder="Message — specs, packaging, target price..."
                value={form.message}
                onChange={set("message")}
                className={`${inputCls} resize-none sm:col-span-2`}
              />
            </div>
            <button
              data-testid="quote-form-submit-button"
              type="submit"
              disabled={sending}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 text-sm font-semibold text-night transition-all duration-300 hover:scale-[1.01] hover:bg-amberwarm disabled:opacity-60 sm:w-auto sm:px-10"
            >
              {sending ? "Sending..." : "Request Quote"}
              <Send size={15} />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
